/**
 * SharedFolderView — view-only drive view rendered inside ShareViewerChrome
 * when the share root is a folder.
 *
 * Per the V1 design (2026-06-10):
 *   - All folder shares (generic + .workpapan + .miniapp) render the
 *     SAME view-only browser; edit-perm becomes a banner hint, not an
 *     in-place affordance
 *   - No rename/delete/move/upload context menu — read-only by
 *     construction (matches backend endpoint shape)
 *   - Open a file → the chrome overlays it IN PLACE (this listing stays
 *     mounted; no new tab, no document load) — `onOpenFile`
 *   - Open a folder → navigate in-place (query param)
 *   - Open a `.shared` / `.chain` mount → navigate INTO it in-place (#188 browse
 *     the resharer-capped reshared view; was the old CrossTenantMountDialog)
 *   - Breadcrumbs sliced to start at the share root (set by backend)
 *
 * Phone viewports get a single TAP to open and a two-line row (name over
 * size · modified) — the desktop table's dblclick + metadata columns don't
 * survive a 375px screen, and a double-tap is eaten by the browser as zoom.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2, ChevronRight, Folder, Play } from 'lucide-react'
import { FileIcon, fileIconProps, miniappIconProps, useToastOptional } from '@papan/core-ui/ui'
import { PointerNeedsApprovalDialog } from '@papan/files/ui/pointer-approval'
import { isProjectFolder, isMiniappFolder, isPointerRow } from '@papan/types'
import { getApiBase, sessionFetch, errorText } from '@papan/api-client'   // #1316: an answer after the share session ended is dropped
import { useTranslation } from '@papan/i18n'
import { useIsPhone } from '../../hooks/useIsPhone'
import type { SharedListItem, SharedListResponse } from './types'

interface Props {
  sessionId: string
  rootFileId: string
  rootName: string
  /** The root's kind when it is a `.shared` / `.chain` — the root crumb carries the corner badge (D27). */
  rootFileType?: string | null
  themeClass: string
  /** Handler that kicks off the existing /:token/mount flow on the
   *  chrome side. The `.shared` click-gate dialog invokes it. */
  onAddToDrive: () => void
  addState: 'idle' | 'busy' | 'success' | 'error'
  addMessage: string | null
  /** Open a FILE in place — the chrome mints its sub-session and overlays the
   *  viewer without unmounting this listing. */
  onOpenFile: (fileId: string) => void
  /** The file currently open in the overlay (highlights its row). */
  activeFileId: string | null
  /** (#757) Enter PDApp mode for a nested `.pdapp` folder row — the chrome sets
   *  `?pdapp=<id>` and lands on the running app. Absent ⇒ a PDApp row browses
   *  in like any folder (the pre-#757 behaviour). */
  onOpenPdapp?: (folderId: string) => void
}

/** A real (native) PDApp folder row — not a `.shared`/`.chain` mount of one,
 *  which keeps its mount browse semantics (#188). */
function isPdappRow(f: SharedListItem): boolean {
  return f.isFolder && !isPointerRow(f) && isMiniappFolder(f.name)
}

// Drive view's FileType has lots of variants; for the view-only listing
// we just need to know if it's a folder and pick an icon. FileIcon
// already does that from the name extension.
function rowType(item: SharedListItem): string {
  // A LIVE pointer only (kind AND suffix — the drive's one classifier, I110): a row renamed off
  // its suffix is a plain file. Both kinds take the same icon overlay.
  if (isPointerRow(item)) return 'shared'
  if (item.isFolder) {
    if (isProjectFolder(item.name)) return 'workpapan'
    return 'folder'
  }
  return 'file'
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const sameYear = d.getFullYear() === now.getFullYear()
    const m = d.toLocaleString('en-US', { month: 'short' })
    return sameYear ? `${m} ${d.getDate()}` : `${m} ${d.getDate()}, ${d.getFullYear()}`
  } catch {
    return ''
  }
}

/** #1264 — the chrome failed to open a listed row with 403 / 404 (`detail.fileId`): the listing re-reads. */
export const SHARE_ROW_REFUSED_EVENT = 'papan:share-row-refused'

export function SharedFolderView({
  sessionId, rootFileId, rootName, rootFileType,
  onOpenFile, activeFileId, onOpenPdapp,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const parentIdParam = searchParams.get('parentId') || rootFileId
  const isPhone = useIsPhone()
  const toast = useToastOptional()
  const { t } = useTranslation('files')
  const [pendingPointerInfo, setPendingPointerInfo] = useState<{ name: string; isFolder: boolean } | null>(null)

  const [listing, setListing] = useState<SharedListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Only the LATEST listing request may land: a quick folder change (or a re-entered view
  // restoring its folder right after mount) must not be overwritten by an older, slower one.
  const listingGenRef = useRef(0)
  // `silent` (#1264's re-read): no loading state, and a failed read leaves the listing as it is — never
  // an error screen over a view that was fine. It takes no generation of its own, so any navigation
  // started meanwhile wins over it.
  const fetchListing = useCallback(async (parentId: string, silent = false) => {
    const gen = silent ? listingGenRef.current : ++listingGenRef.current
    const stale = () => gen !== listingGenRef.current
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const res = await sessionFetch(
        `${getApiBase()}/api/share-links/session/${sessionId}/list?parentId=${encodeURIComponent(parentId)}`,
        { credentials: 'include' },
      )
      if (stale()) return
      if (!res.ok) {
        if (silent) return
        const body = await res.json().catch(() => ({}))
        if (stale()) return
        // Backend returns 409 cross_tenant_mount_not_traversable when the
        // recipient tries to drill into a .shared row via this endpoint —
        // shouldn't happen via UI (dialog intercepts the click) but if
        // someone hand-edits the URL, surface a friendly message.
        if (res.status === 409 && body.error === 'cross_tenant_mount_not_traversable') {
          setError('That folder lives in another organization. Add this share to your Drive to access it.')
        } else if (res.status === 403) {
          setError("That folder isn't part of this share.")
        } else {
          setError(body.error || `Couldn't load (${res.status})`)
        }
        return
      }
      const body = (await res.json()) as SharedListResponse
      if (stale()) return
      setListing(body)
    } catch (err) {
      // #1316: a dropped answer (its session ended) is no error — the listing keeps what it shows
      if (!stale() && !silent) { const text = errorText(err, 'Network error'); if (text !== null) setError(text) }
    } finally {
      if (!stale() && !silent) setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    void fetchListing(parentIdParam)
  }, [parentIdParam, fetchListing])

  // #1264 — the chrome could not open a row of this listing (403 / 404 — it may be gone, its delete
  // never announced; this view has no realtime): re-read the folder. Only when it lists that row.
  const listingRef = useRef(listing)
  listingRef.current = listing
  // A SILENT re-read; one already running is not started again.
  const silentRereadRef = useRef(false)
  useEffect(() => {
    const onRefused = (e: Event) => {
      const fileId = (e as CustomEvent<{ fileId?: string }>).detail?.fileId
      if (!fileId || !listingRef.current?.files?.some((f) => f.id === fileId) || silentRereadRef.current) return
      silentRereadRef.current = true
      void fetchListing(parentIdParam, true).finally(() => { silentRereadRef.current = false })
    }
    window.addEventListener(SHARE_ROW_REFUSED_EVENT, onRefused)
    return () => window.removeEventListener(SHARE_ROW_REFUSED_EVENT, onRefused)
  }, [parentIdParam, fetchListing])

  const navigateToFolder = useCallback((id: string) => {
    const next = new URLSearchParams(searchParams)
    if (id === rootFileId) {
      next.delete('parentId')
    } else {
      next.set('parentId', id)
    }
    // Changing folder closes any open file — `?file` is scoped to the folder we
    // opened it from, and carrying it across would leave the overlay showing a
    // file that isn't in the listing behind it.
    next.delete('file')
    setSearchParams(next, { replace: false })
  }, [searchParams, setSearchParams, rootFileId])

  // Opening a row. Files hand off to the chrome, which mints the file's
  // sub-session and overlays the viewer WITHOUT unmounting this listing — so
  // closing the file drops us right back here (see ShareBody's folder branch).
  // This used to `window.open` a new tab: a full document load of the whole SPA
  // just to read one file, and on touch it was unreachable at all (the row only
  // had a dblclick handler, and a double-tap is eaten by the browser as zoom).
  const openRow = useCallback((file: SharedListItem) => {
    // A DEAD pointer is a greyed, unopenable row — the drive's words, not an empty folder.
    if (file.shareBroken) {
      toast?.info(file.shareNoAccess ? t('shareView.deadPointer.sharedNoAccessToast') : t('shareView.deadPointer.sharedRevokedToast'))
      return
    }
    if (file.chainBroken) {
      toast?.info(t('shareView.deadPointer.chainGoneToast'))
      return
    }
    // D17 — a `.chain` that Needs approval doesn't open, as in the drive. There is no Approve in
    // the share view, so everyone gets the reason (a person who may approve does it in their drive).
    if (file.chainNeedsApproval) {
      setPendingPointerInfo({ name: file.name, isFolder: file.isFolder })
      return
    }
    // #188 — a `.shared`/`.chain` mount presents as a folder; clicking it BROWSES INTO
    // the resharer-capped reshared view (the /list endpoint composes it), consistent with
    // the drive. Pre-#188 this popped the CrossTenantMountDialog ("add to drive to access")
    // because the mount wasn't traversable in place; that add-to-drive affordance still
    // lives at the chrome level (ShareViewerChrome's "Add to Drive").
    // (#757) A nested PDApp opens AS AN APP — the same PDApp mode a PDApp-root
    // share lands in — rather than as a plain listing of its internals.
    if (onOpenPdapp && isPdappRow(file)) {
      onOpenPdapp(file.id)
      return
    }
    if (file.isFolder || isPointerRow(file)) {
      navigateToFolder(file.id)
      return
    }
    onOpenFile(file.id)
  }, [navigateToFolder, onOpenFile, onOpenPdapp, toast, t])

  return (
    <div className="flex flex-col h-full">
      {pendingPointerInfo !== null && (
        <PointerNeedsApprovalDialog fileName={pendingPointerInfo.name} isFolder={pendingPointerInfo.isFolder} onClose={() => setPendingPointerInfo(null)} />
      )}
      <Breadcrumbs
        breadcrumbs={listing?.breadcrumbs ?? [{ id: rootFileId, name: rootName }]}
        rootFileId={rootFileId}
        rootFileType={rootFileType ?? null}
        onNavigate={navigateToFolder}
      />

      {loading && !listing ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={18} className="themed-text-muted animate-spin" />
        </div>
      ) : error ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <Folder size={28} className="themed-text-muted mb-3" />
          <p className="text-sm themed-text-muted max-w-md text-center">{error}</p>
        </div>
      ) : !listing || listing.files.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <Folder size={28} className="themed-text-muted mb-3" />
          <p className="text-sm themed-text-muted">This folder is empty.</p>
        </div>
      ) : (
        <FileList
          files={listing.files}
          onOpen={openRow}
          isPhone={isPhone}
          activeFileId={activeFileId}
          onRunPdapp={onOpenPdapp}
        />
      )}

    </div>
  )
}

function Breadcrumbs({
  breadcrumbs, rootFileId, rootFileType, onNavigate,
}: {
  breadcrumbs: Array<{ id: string; name: string }>
  rootFileId: string
  rootFileType?: string | null
  onNavigate: (id: string) => void
}) {
  return (
    <div className="flex items-center gap-1 px-4 py-2 text-xs themed-text-muted border-b themed-border">
      <button
        type="button"
        onClick={() => onNavigate(rootFileId)}
        className="inline-flex items-center gap-1 hover:themed-text"
      >
        {rootFileType
          ? <FileIcon name={breadcrumbs[0]?.name ?? ''} isFolder={true} fileType={rootFileType} size={12} />
          : <Folder size={12} />}
        <span className="font-medium">{breadcrumbs[0]?.name ?? 'Shared folder'}</span>
      </button>
      {breadcrumbs.slice(1).map((b, idx) => {
        const isLast = idx === breadcrumbs.length - 2
        return (
          <span key={b.id} className="inline-flex items-center gap-1">
            <ChevronRight size={12} className="themed-text-muted" />
            {isLast ? (
              <span className="themed-text">{b.name}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(b.id)}
                className="hover:themed-text"
              >
                {b.name}
              </button>
            )}
          </span>
        )
      })}
    </div>
  )
}

function FileList({
  files, onOpen, isPhone, activeFileId, onRunPdapp,
}: {
  files: SharedListItem[]
  onOpen: (file: SharedListItem) => void
  isPhone: boolean
  activeFileId: string | null
  /** (#757) Per-row Run on a nested PDApp. Opening the PDApp already lands on
   *  the running app (workspace parity), so Run is that same entry — surfaced as
   *  a single-click button so a desktop user needn't know rows open on
   *  double-click. Settings is NOT per-row: it needs a per-PDApp native-access
   *  lookup, so it lives in the PDApp panel header once the app is open. */
  onRunPdapp?: (folderId: string) => void
}) {
  const { t } = useTranslation('files')
  // Folders first, then files; both alphabetical. Backend already
  // sorts this way (default sortBy=name + is_folder DESC in SQL),
  // but we leave it tolerant for future sort-by params.
  //
  // Touch opens on a single TAP; pointer keeps the desktop dblclick (a
  // single-click there is a selection gesture, and this listing has no
  // selection model — so click-to-open would fire on every stray click).
  const openProps = (f: SharedListItem) => isPhone
    ? { onClick: () => onOpen(f) }
    : { onDoubleClick: () => onOpen(f) }

  return (
    <div className="flex-1 overflow-auto overscroll-contain">
      {/* The metadata columns don't fit a phone — the row carries them on a
          second line instead (mirrors the Drive's mobile rows). */}
      {!isPhone && (
        <div className="px-4 py-1.5 border-b themed-border text-[11px] uppercase tracking-wide themed-text-muted flex items-center">
          <span className="flex-1">Name</span>
          <span className="w-32 text-right">Modified</span>
          <span className="w-20 text-right">Size</span>
        </div>
      )}
      {files.map(f => {
        const isMount = isPointerRow(f)
        const broken = !!(f.shareBroken || f.chainBroken)
        const pdapp = isPdappRow(f)
        const active = f.id === activeFileId
        const meta = [f.isFolder ? '' : formatSize(f.size), formatDate(f.updatedAt)]
          .filter(Boolean).join(' · ')
        return (
          <div
            key={f.id}
            {...openProps(f)}
            className={`flex items-center border-b themed-row-border cursor-pointer select-none group ${
              isPhone ? 'px-3 py-2.5 active:themed-bg-tertiary' : 'h-12 px-4 themed-row-hover'
            } ${active ? 'themed-bg-tertiary' : ''} ${broken ? 'opacity-50' : ''}`}
            title={broken ? (f.shareNoAccess ? t('shareView.deadPointer.noAccess') : t('shareView.deadPointer.noLongerAvailable')) : undefined}
            data-file-id={f.id}
            {...(broken ? { 'data-broken': 'true' } : {})}
            data-file-type={isMount ? 'shared' : pdapp ? 'pdapp' : f.isFolder ? 'folder' : 'file'}
          >
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <FileIcon
                {...fileIconProps({ ...f, type: rowType(f) })}
                size={isPhone ? 20 : 16}
                // The PDApp's REAL custom icon (#757) — the same spread the
                // share tree (ShareFileExplorer) and the Drive use. Without it a
                // nested PDApp fell back to the generic app glyph here while the
                // tree beside it showed the author's icon.
                {...miniappIconProps(f)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm themed-text truncate">{f.name}</span>
                  {broken && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full themed-bg-tertiary themed-text-muted shrink-0 whitespace-nowrap">
                      {f.shareNoAccess ? t('shareView.deadPointer.noAccess') : t('shareView.deadPointer.noLongerAvailable')}
                    </span>
                  )}
                  {isMount && !broken && f.sharedFromName && !isPhone && (
                    <span className="text-[10px] themed-text-muted px-1.5 py-0.5 rounded bg-white/5 shrink-0">
                      Shared from {f.sharedFromName}
                    </span>
                  )}
                </div>
                {isPhone && (
                  <div className="truncate text-xs themed-text-muted">
                    {isMount && f.sharedFromName ? `Shared from ${f.sharedFromName}` : meta}
                  </div>
                )}
              </div>
            </div>
            {pdapp && onRunPdapp && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRunPdapp(f.id) }}
                onDoubleClick={(e) => e.stopPropagation()}
                aria-label={`Run ${f.name}`}
                title="Run app"
                className={`shrink-0 mr-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs themed-text-muted hover:themed-text hover:themed-bg-tertiary ${
                  isPhone ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
                }`}
              >
                <Play size={12} />
                <span>Run</span>
              </button>
            )}
            {!isPhone && (
              <>
                <span className="w-32 text-right text-xs themed-text-muted">
                  {formatDate(f.updatedAt)}
                </span>
                <span className="w-20 text-right text-xs themed-text-muted">
                  {f.isFolder ? '—' : formatSize(f.size)}
                </span>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
